import { Component } from '@theme/component';

const POLL_INTERVAL = 30_000;
const REQUEST_TIMEOUT = 8_000;

/**
 * Reads an aggregate produced by paid-order processing. Never simulates sales or
 * increments from a cart, clock, storage, or page view. A real increase rolls the
 * changed digits once; initial values and corrections are shown without motion.
 * @extends {Component<{digits: HTMLElement, announcement: HTMLElement}>}
 */
class ProjectPetSalesCounter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.stop();
    this.cleanup = new AbortController();
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.total = this.readTotal(this.dataset.total);
    if (this.total === null || !this.refs.digits || !this.refs.announcement) return;
    try {
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en');
    } catch {
      this.formatter = new Intl.NumberFormat('en');
    }
    this.renderTotal(this.total, false);
    const options = { signal: this.cleanup.signal };
    document.addEventListener('visibilitychange', () => this.updateActivity(), options);
    window.addEventListener('focus', () => this.updateActivity(), options);
    document.addEventListener('projectpet:motion-change', () => this.renderTotal(this.total, false), options);
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) this.renderTotal(this.total, false);
    }, options);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.updateActivity();
      });
      this.observer.observe(this);
    } else {
      this.inView = true;
      this.updateActivity();
    }
  }

  disconnectedCallback() {
    this.stop();
    super.disconnectedCallback();
  }

  stop() {
    this.cleanup?.abort();
    this.observer?.disconnect();
    this.request?.abort();
    this.request = null;
    clearTimeout(this.timer);
    clearTimeout(this.requestTimer);
    this.animations?.forEach((animation) => animation.cancel());
    this.animations = [];
    this.inView = false;
  }

  readTotal(raw) {
    if (typeof raw !== 'string' || !/^\d+$/.test(raw)) return null;
    const value = Number(raw);
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  get active() {
    return this.isConnected && this.inView && !document.hidden;
  }

  updateActivity() {
    clearTimeout(this.timer);
    if (!this.active) {
      this.request?.abort();
      return;
    }
    this.refresh();
  }

  async refresh() {
    if (!this.active || this.request) return;
    const request = new AbortController();
    this.request = request;
    const timeout = setTimeout(() => request.abort(), REQUEST_TIMEOUT);
    this.requestTimer = timeout;
    try {
      const url = new URL(this.dataset.sectionUrl || '/', window.location.origin);
      url.searchParams.set('section_id', 'pp-sales-total');
      url.searchParams.set('_pp_sales', String(Math.floor(Date.now() / POLL_INTERVAL)));
      const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      // Fetch only the aggregate: no section morph or private storefront API needed.
      const response = await fetch(url, { signal: request.signal, cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) return;
      const html = await response.text();
      if (!this.active || request.signal.aborted) return;
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const next = this.readTotal(fragment.querySelector('[data-pp-sales-total]')?.getAttribute('data-pp-sales-total'));
      if (next === null || next === this.total) return;
      this.renderTotal(next, next > this.total && !this.motion.matches && document.documentElement.dataset.ppMotionPaused !== 'true');
      this.total = next;
      this.dataset.total = String(next);
    } catch {
      // Network failure retains the last server-confirmed total, without motion.
    } finally {
      clearTimeout(timeout);
      if (this.request === request) {
        this.request = null;
        if (this.active) this.timer = setTimeout(() => this.refresh(), POLL_INTERVAL);
      }
    }
  }

  renderTotal(total, animate) {
    this.animations?.forEach((animation) => animation.cancel());
    this.animations = [];
    const text = this.formatter.format(total);
    const previous = Array.from(this.refs.digits.textContent || '');
    const characters = Array.from(text);
    const offset = previous.length - characters.length;
    const fragment = document.createDocumentFragment();
    characters.forEach((character, index) => {
      const digit = document.createElement('span');
      digit.className = 'pp-sales-counter__digit';
      const value = document.createElement('span');
      value.className = 'pp-sales-counter__value';
      value.textContent = character;
      digit.append(value);
      const oldCharacter = previous[index + offset];
      if (animate && oldCharacter !== character && typeof value.animate === 'function') {
        const oldValue = document.createElement('span');
        oldValue.className = 'pp-sales-counter__previous';
        oldValue.textContent = oldCharacter || '';
        digit.append(oldValue);
        const timing = { duration: 420, easing: 'cubic-bezier(.2,.7,.2,1)' };
        const incoming = value.animate([{ transform: 'translateY(-100%)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], timing);
        const outgoing = oldValue.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(100%)', opacity: 0 }], timing);
        outgoing.onfinish = () => oldValue.remove();
        this.animations.push(incoming, outgoing);
      }
      fragment.append(digit);
    });
    this.refs.digits.replaceChildren(fragment);
    this.refs.announcement.textContent = (this.dataset.labelTemplate || '[count]').replace('[count]', text);
  }
}

if (!customElements.get('pp-sales-counter')) customElements.define('pp-sales-counter', ProjectPetSalesCounter);
